import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Printer, MoreHorizontal, Copy, ChevronDown, Truck,
  Package, CheckCircle, CreditCard, MapPin, Mail, Phone, User,
  Plus, MessageSquare, Globe, Check, X, Edit2, AlertCircle,
  Clock, ExternalLink, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}
function fmtDate(iso, short = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (short) return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// ── Status maps ────────────────────────────────────────────────────────────
const ORDER_STATUS_MAP = {
  placed:            { label: "Placed",           color: "#1d4ed8", bg: "#dbeafe" },
  accepted:          { label: "Processing",        color: "#6d28d9", bg: "#ede9fe" },
  packed:            { label: "Packed",            color: "#0e7490", bg: "#cffafe" },
  partially_shipped: { label: "Partially Shipped", color: "#b45309", bg: "#fef3c7" },
  shipped:           { label: "Shipped",           color: "#166534", bg: "#dcfce7" },
  out_for_delivery:  { label: "Out for Delivery",  color: "#c2410c", bg: "#ffedd5" },
  delivered:         { label: "Delivered",         color: "#15803d", bg: "#dcfce7" },
  rejected:          { label: "Rejected",          color: "#b91c1c", bg: "#fee2e2" },
  cancelled:         { label: "Cancelled",         color: "#475569", bg: "#f1f5f9" },
};
const PAYMENT_MAP = {
  pending:  { label: "COD / Pending", color: "#92400e", bg: "#fef3c7" },
  paid:     { label: "Paid",          color: "#166534", bg: "#dcfce7" },
  failed:   { label: "Failed",        color: "#991b1b", bg: "#fee2e2" },
  refunded: { label: "Refunded",      color: "#475569", bg: "#f1f5f9" },
};
const ITEM_STATUS_MAP = {
  placed:   { label: "Pending",    subLabel: "Not Shipped",   color: "#b45309", bg: "#fef3c7" },
  accepted: { label: "Processing", subLabel: "In Progress",   color: "#6d28d9", bg: "#ede9fe" },
  packed:   { label: "Ready",      subLabel: "Ready to Ship", color: "#0e7490", bg: "#cffafe" },
  shipped:  { label: "Shipped",    subLabel: "",              color: "#166534", bg: "#dcfce7" },
  cancelled:{ label: "Cancelled",  subLabel: "",              color: "#475569", bg: "#f1f5f9" },
};

const COURIER_OPTIONS = ["Blue Dart", "Delhivery", "DTDC", "FedEx", "Ekart", "Shadowfax", "XpressBees", "Ecom Express", "Other"];
const ORDER_STEP_KEYS = ["placed", "accepted", "packed", "partially_shipped", "shipped", "out_for_delivery", "delivered"];

function StatusPill({ status, map = ORDER_STATUS_MAP, size = "sm" }) {
  const cfg = map[status] || { label: status || "—", color: "#64748b", bg: "#f1f5f9" };
  const px = size === "sm" ? "px-2.5 py-0.5 text-[11px]" : "px-3 py-1 text-xs";
  return (
    <span className={`inline-flex items-center gap-1 font-bold rounded-full ${px}`}
      style={{ background: cfg.bg, color: cfg.color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

// ── Top info strip cards ───────────────────────────────────────────────────
function InfoCard({ label, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl px-4 py-3 flex-1 min-w-0 ${className}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</p>
      {children}
    </div>
  );
}

// ── Status stepper ─────────────────────────────────────────────────────────
const STEPS = [
  { key: "placed",    label: "Order Placed",       sub: "placed" },
  { key: "payment",   label: "Payment Confirmed",   sub: "paid" },
  { key: "accepted",  label: "Processing",          sub: "accepted" },
  { key: "shipped",   label: "Shipped",             sub: "shipped" },
  { key: "out_for_delivery", label: "Out for Delivery", sub: "out_for_delivery" },
  { key: "delivered", label: "Delivered",           sub: "delivered" },
];

function StatusStepper({ order }) {
  const statusIdx = ORDER_STEP_KEYS.indexOf(order.order_status);
  const isPaid = order.payment_status === "paid";

  const stepStates = STEPS.map((s, i) => {
    if (s.key === "payment") return isPaid ? "done" : (statusIdx >= 1 ? "pending" : "pending");
    const sIdx = ORDER_STEP_KEYS.indexOf(s.key === "payment" ? "accepted" : s.key);
    if (sIdx < 0) return "pending";
    if (statusIdx > sIdx) return "done";
    if (statusIdx === sIdx) return "current";
    return "pending";
  });

  // Fix: placed is always done once we have an order
  stepStates[0] = "done";
  if (isPaid) stepStates[1] = "done";

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-6 py-4 mb-4 relative overflow-hidden">
      <div className="flex items-start relative">
        {/* connector line */}
        <div className="absolute top-4 left-6 right-6 h-0.5 bg-slate-200" />
        <div className="absolute top-4 left-6 h-0.5 bg-indigo-500 transition-all duration-700"
          style={{ width: `${Math.max(0, (stepStates.filter(s => s === "done").length - 1) / (STEPS.length - 1)) * 100}%` }} />

        {STEPS.map((step, i) => {
          const state = stepStates[i];
          return (
            <div key={step.key} className="flex-1 flex flex-col items-center z-10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                state === "done"    ? "bg-indigo-600 border-indigo-600 shadow shadow-indigo-200" :
                state === "current" ? "bg-white border-indigo-500 shadow shadow-indigo-100" :
                                      "bg-white border-slate-300"
              }`}>
                {state === "done"
                  ? <Check className="w-3.5 h-3.5 text-white" />
                  : state === "current"
                    ? <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse" />
                    : <div className="w-2 h-2 rounded-full bg-slate-300" />}
              </div>
              <p className={`text-[10px] font-semibold mt-2 text-center leading-tight ${
                state === "done" || state === "current" ? "text-slate-800" : "text-slate-400"
              }`}>{step.label}</p>
              {state === "current" ? (
                <p className="text-[9px] text-indigo-500 font-bold mt-0.5">Active</p>
              ) : state === "done" ? (
                <p className="text-[9px] text-slate-400 mt-0.5">{fmtDate(order.created_at, true)}</p>
              ) : (
                <p className="text-[9px] text-slate-300 mt-0.5">Pending</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Per-item inline row editor ─────────────────────────────────────────────
const ITEM_STATUSES_OPTS = [
  { value: "placed",    label: "Pending"    },
  { value: "accepted",  label: "Accepted"   },
  { value: "packed",    label: "Packed"     },
  { value: "shipped",   label: "Shipped"    },
  { value: "cancelled", label: "Cancelled"  },
];

function ItemRowEditor({ item, itemIndex, orderId, token, defaultStatus, onSaved, onClose }) {
  const [status, setStatus] = useState(defaultStatus || item.item_status || "placed");
  // A courier typed in under "Other" is saved as its real name, so emails and labels show that name
  const savedCourier = item.item_courier || "";
  const [courier, setCourier] = useState(!savedCourier || COURIER_OPTIONS.includes(savedCourier) ? savedCourier : "Other");
  const [otherCourier, setOtherCourier] = useState(COURIER_OPTIONS.includes(savedCourier) ? "" : savedCourier);
  const [trackingNo, setTrackingNo] = useState(item.item_tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(item.item_tracking_url || "");
  const [saving, setSaving] = useState(false);

  const showTracking = status === "shipped";

  const handleSave = async () => {
    const courierName = courier === "Other" ? otherCourier.trim() : courier;
    if (showTracking && courier === "Other" && !courierName) {
      toast.error("Enter the courier partner's name");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/orders/${orderId}/items/${itemIndex}`,
        { item_status: status, item_courier: courierName || courier, item_tracking_number: trackingNo, item_tracking_url: trackingUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Item status updated");
      onSaved(res.data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <tr className="bg-indigo-50/60 border-t border-indigo-100">
      <td colSpan={10} className="px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Status</label>
            <div className="flex gap-1.5 flex-wrap">
              {ITEM_STATUSES_OPTS.map(s => (
                <button key={s.value} type="button" onClick={() => setStatus(s.value)}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all"
                  style={status === s.value
                    ? { background: ITEM_STATUS_MAP[s.value]?.bg, color: ITEM_STATUS_MAP[s.value]?.color, borderColor: ITEM_STATUS_MAP[s.value]?.color }
                    : { background: "#f8fafc", color: "#64748b", borderColor: "#e2e8f0" }}
                  data-testid={`item-status-${s.value}-${itemIndex}`}
                >{s.label}</button>
              ))}
            </div>
          </div>
          {showTracking && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Courier</label>
                <select value={courier} onChange={e => setCourier(e.target.value)}
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-indigo-400 bg-white min-w-[130px]"
                  data-testid={`item-courier-${itemIndex}`}>
                  <option value="">Select courier</option>
                  {COURIER_OPTIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              {courier === "Other" && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Courier Name</label>
                  <input value={otherCourier} onChange={e => setOtherCourier(e.target.value)}
                    placeholder="e.g. Amazon Shipping" maxLength={60}
                    className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-indigo-400 w-44"
                    data-testid={`item-courier-other-${itemIndex}`} />
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Tracking Number</label>
                <input value={trackingNo} onChange={e => setTrackingNo(e.target.value)}
                  placeholder="BD12345678"
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-indigo-400 w-40"
                  data-testid={`item-tracking-number-${itemIndex}`} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Tracking URL</label>
                <input value={trackingUrl} onChange={e => setTrackingUrl(e.target.value)}
                  placeholder="https://track.bluedart.com/…"
                  className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-indigo-400 w-52" />
              </div>
            </>
          )}
          <div className="flex items-center gap-2 pb-0.5">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
              data-testid={`item-save-btn-${itemIndex}`}>
              {saving ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : <Check className="w-3 h-3" />}
              Save
            </button>
            <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700 font-medium px-2 py-1.5">Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Items Tab ──────────────────────────────────────────────────────────────
function ItemsTab({ order, token, onOrderUpdated }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [expandMode, setExpandMode] = useState("update"); // "update" | "ship"
  const [selected, setSelected] = useState(new Set());
  const [bulkCourier, setBulkCourier] = useState("Blue Dart");
  const [bulkTracking, setBulkTracking] = useState("");
  const [bulkOtherCourier, setBulkOtherCourier] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const items = order.items || [];

  const toggleSelect = (i) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(i) ? s.delete(i) : s.add(i);
      return s;
    });
  };
  const toggleAll = () => {
    setSelected(selected.size === items.length ? new Set() : new Set(items.map((_, i) => i)));
  };

  const openEditor = (idx, mode) => {
    if (expandedIdx === idx && expandMode === mode) { setExpandedIdx(null); return; }
    setExpandedIdx(idx);
    setExpandMode(mode);
  };

  const handleBulkShip = async () => {
    if (selected.size === 0) { toast.error("Select at least one item"); return; }
    if (!bulkTracking) { toast.error("Enter a tracking number"); return; }
    const courierName = bulkCourier === "Other" ? bulkOtherCourier.trim() : bulkCourier;
    if (!courierName) { toast.error("Enter the courier partner's name"); return; }
    setBulkSaving(true);
    try {
      let lastRes = null;
      for (const idx of selected) {
        const res = await axios.patch(
          `${API}/admin/orders/${order.id}/items/${idx}`,
          { item_status: "shipped", item_courier: courierName, item_tracking_number: bulkTracking },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        lastRes = res.data;
      }
      toast.success(`${selected.size} item(s) marked as shipped`);
      setSelected(new Set());
      setBulkTracking("");
      if (lastRes) onOrderUpdated(lastRes);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBulkSaving(false); }
  };

  const shippedCount = items.filter(it => it.item_status === "shipped").length;

  return (
    <div className="space-y-4">
      {/* Items table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-900">Order Items
            <span className="ml-1.5 text-xs font-normal text-slate-400">({items.length} items · {shippedCount} shipped)</span>
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox" checked={selected.size === items.length && items.length > 0}
                    onChange={toggleAll} className="rounded border-slate-300 cursor-pointer" />
                </th>
                <th className="px-2 py-2.5 text-left font-semibold text-slate-500 w-6">#</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-500 min-w-[200px]">Product Details</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-500">SKU / Variant</th>
                <th className="px-3 py-2.5 text-right font-semibold text-slate-500">Price</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-500">Qty</th>
                <th className="px-3 py-2.5 text-right font-semibold text-slate-500">Total</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-500 min-w-[110px]">Item Status</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-500 min-w-[140px]">Tracking Details</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const isCfg = ITEM_STATUS_MAP[item.item_status || "placed"];
                const isExpanded = expandedIdx === i;
                const isShipped = item.item_status === "shipped";
                return (
                  <>
                    <tr key={i}
                      className={`border-b border-slate-100 transition-colors ${isExpanded ? "bg-indigo-50/40" : "hover:bg-slate-50"}`}
                      data-testid={`order-item-row-${i}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelect(i)}
                          className="rounded border-slate-300 cursor-pointer" />
                      </td>
                      <td className="px-2 py-3 text-slate-400 font-medium">{i + 1}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0 bg-slate-50">
                            {item.image || item.product_image
                              ? <img src={item.image || item.product_image} alt="" className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-slate-300" /></div>}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 line-clamp-1">{item.product_name || item.name}</p>
                            {item.variant && <p className="text-[10px] text-slate-400 mt-0.5">{item.variant}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-500 font-mono">{item.sku || "—"}</td>
                      <td className="px-3 py-3 text-right font-semibold text-slate-700">₹{fmt(item.price || 0)}</td>
                      <td className="px-3 py-3 text-center text-slate-700">{item.quantity}</td>
                      <td className="px-3 py-3 text-right font-bold text-slate-900">₹{fmt(item.amount || (item.price * item.quantity))}</td>
                      <td className="px-3 py-3">
                        <div>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: isCfg.bg, color: isCfg.color }}>
                            {isCfg.label}
                          </span>
                          {isCfg.subLabel && <p className="text-[9px] text-slate-400 mt-0.5">{isCfg.subLabel}</p>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {isShipped && item.item_tracking_number ? (
                          <div>
                            <p className="text-[10px] font-semibold text-slate-700">{item.item_courier}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[10px] font-mono text-slate-500">{item.item_tracking_number}</span>
                              <button onClick={() => { navigator.clipboard.writeText(item.item_tracking_number); toast.success("Copied!"); }}
                                className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0">
                                <Copy className="w-2.5 h-2.5" />
                              </button>
                            </div>
                            {item.item_tracking_url && (
                              <a href={item.item_tracking_url} target="_blank" rel="noopener noreferrer"
                                className="text-[9px] text-indigo-600 hover:underline flex items-center gap-0.5 mt-0.5">
                                Track <ExternalLink className="w-2 h-2" />
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {item.item_status === "cancelled" ? null : isShipped ? (
                          <button onClick={() => openEditor(i, "update")}
                            className="text-[11px] font-semibold px-2.5 py-1 border border-slate-300 rounded-lg text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-colors"
                            data-testid={`item-update-btn-${i}`}>
                            Update
                          </button>
                        ) : (
                          <button onClick={() => openEditor(i, "ship")}
                            className="text-[11px] font-bold px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm"
                            data-testid={`item-ship-btn-${i}`}>
                            Mark as Shipped
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <ItemRowEditor
                        key={`editor-${i}`}
                        item={item}
                        itemIndex={i}
                        orderId={order.id}
                        token={token}
                        defaultStatus={expandMode === "ship" ? "shipped" : undefined}
                        onSaved={(data) => { onOrderUpdated(data); setExpandedIdx(null); }}
                        onClose={() => setExpandedIdx(null)}
                      />
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk shipping actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Truck className="w-3.5 h-3.5 text-indigo-500" /> Item Level Shipping Actions
          {selected.size > 0 && <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-1.5 py-0.5 rounded-full">{selected.size} selected</span>}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Select Courier</label>
            <select value={bulkCourier} onChange={e => setBulkCourier(e.target.value)}
              className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400 bg-white min-w-[140px]">
              {COURIER_OPTIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          {bulkCourier === "Other" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Courier Name</label>
              <input value={bulkOtherCourier} onChange={e => setBulkOtherCourier(e.target.value)}
                placeholder="e.g. Amazon Shipping" maxLength={60}
                className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400 w-48"
                data-testid="bulk-courier-other" />
            </div>
          )}
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Tracking Number</label>
            <input value={bulkTracking} onChange={e => setBulkTracking(e.target.value)}
              placeholder="Enter tracking number"
              className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400" />
          </div>
          <button onClick={handleBulkShip} disabled={bulkSaving || selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors shadow-sm whitespace-nowrap"
            data-testid="bulk-ship-btn">
            {bulkSaving ? <div className="w-3.5 h-3.5 rounded-full border border-white/40 border-t-white animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
            Mark Selected as Shipped
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Customer & Shipping Tab ────────────────────────────────────────────────
function CustomerTab({ order }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Customer Details</p>
        {[
          { icon: User,  label: "Name",    value: order.customer_name },
          { icon: Mail,  label: "Email",   value: order.customer_email?.includes(".local") ? "—" : order.customer_email },
          { icon: Phone, label: "Phone",   value: order.customer_phone || "—" },
        ].map(r => (
          <div key={r.label} className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <r.icon className="w-3.5 h-3.5 text-slate-500" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400">{r.label}</p>
              <p className="text-xs font-semibold text-slate-800">{r.value || "—"}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Shipping Address</p>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-3.5 h-3.5 text-slate-500" />
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">{order.delivery_address || "—"}</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Truck className="w-3.5 h-3.5 text-slate-500" />
          </div>
          <div>
            <p className="text-[10px] text-slate-400">Shipping Method</p>
            <p className="text-xs font-semibold text-slate-800">Standard Delivery</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Payment Details Tab ────────────────────────────────────────────────────
function PaymentTab({ order, token, onOrderUpdated }) {
  const [updating, setUpdating] = useState(null);
  const PAYMENT_TRANSITIONS = {
    pending:  [{ key: "paid", label: "Mark Paid", color: "#16a34a" }, { key: "failed", label: "Mark Failed", color: "#dc2626" }],
    paid:     [{ key: "refund_initiated", label: "Initiate Refund", color: "#0369a1" }],
    failed:   [{ key: "pending", label: "Reset to Pending", color: "#64748b" }],
    refund_initiated: [{ key: "refunded", label: "Mark Refunded", color: "#16a34a" }],
  };
  const actions = PAYMENT_TRANSITIONS[order.payment_status] || [];

  const handlePay = async (newPay) => {
    setUpdating(newPay);
    try {
      await axios.put(`${API}/admin/orders/${order.id}/payment`, { payment_status: newPay }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment status updated");
      onOrderUpdated({ ...order, payment_status: newPay });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUpdating(null); }
  };

  const rows = [
    { label: "Method",       value: order.payment_method || "COD" },
    { label: "Amount",       value: `₹${fmt(order.total)}` },
    ...(order.razorpay_payment_id ? [{ label: "Transaction ID", value: order.razorpay_payment_id, mono: true }] : []),
    { label: "Date",         value: order.payment_status === "paid" ? fmtDate(order.updated_at, true) : "Pending" },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 max-w-lg">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Payment Information</p>
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between items-center py-1.5 border-b border-slate-50">
          <span className="text-xs text-slate-500">{r.label}</span>
          <span className={`text-xs font-bold text-slate-800 ${r.mono ? "font-mono" : ""}`}>{r.value}</span>
        </div>
      ))}
      <div className="flex justify-between items-center py-1.5">
        <span className="text-xs text-slate-500">Status</span>
        <StatusPill status={order.payment_status} map={PAYMENT_MAP} />
      </div>
      {actions.length > 0 && (
        <div className="flex gap-2 pt-2">
          {actions.map(a => (
            <button key={a.key} onClick={() => handlePay(a.key)} disabled={!!updating}
              className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
              style={{ background: a.color }}>
              {updating === a.key && <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" />}
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Order Notes Tab ────────────────────────────────────────────────────────
function NotesTab({ order, token, onOrderUpdated }) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/orders/${order.id}/note`, { note: noteText }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Note added");
      setNoteText("");
      onOrderUpdated({ ...order });
    } catch { toast.error("Failed to save note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Internal Notes</p>
          <button onClick={() => setNoteText("")}
            className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">
            <Plus className="w-3 h-3" /> Add Note
          </button>
        </div>
        <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
          rows={3} placeholder="Add an internal note (visible to admins only)…"
          className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-400 resize-none" />
        {noteText.trim() && (
          <button onClick={handleAddNote} disabled={saving}
            className="mt-2 flex items-center gap-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg disabled:opacity-50">
            {saving ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : <Check className="w-3 h-3" />}
            Save Note
          </button>
        )}
        {order.notes?.length > 0 ? (
          <div className="mt-3 space-y-2">
            {order.notes.map((n, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-white">A</span>
                  </div>
                  <span className="text-[10px] font-bold text-slate-700">Admin</span>
                  <span className="text-[9px] text-slate-400 ml-auto">{fmtDate(n.created_at)}</span>
                </div>
                <p className="text-xs text-slate-600 pl-7">{n.text}</p>
              </div>
            ))}
          </div>
        ) : !noteText && <p className="text-xs text-slate-400 mt-3 text-center py-4">No internal notes yet.</p>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Customer Notes</p>
        {order.notes_customer ? (
          <p className="text-xs text-slate-700 bg-slate-50 rounded-xl p-3">{order.notes_customer}</p>
        ) : (
          <div className="text-center py-6 text-slate-400">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No customer notes available.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tracking & Updates Tab ─────────────────────────────────────────────────
function TrackingTab({ order }) {
  const items = order.items || [];
  const shippedItems = items.filter(it => it.item_status === "shipped" && it.item_tracking_number);
  const updates = [
    ...(order.created_at ? [{ text: "Order placed", sub: "Order received", time: order.created_at, color: "bg-blue-500" }] : []),
    ...(order.payment_status === "paid" ? [{ text: "Payment confirmed", sub: `₹${fmt(order.total)} received`, time: order.updated_at, color: "bg-green-500" }] : []),
    ...shippedItems.map(it => ({
      text: `${it.product_name || it.name} shipped`,
      sub: `Tracking: ${it.item_tracking_number} (${it.item_courier || ""})`,
      time: order.updated_at,
      color: "bg-indigo-500",
    })),
  ].reverse();

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Recent Updates</p>
      {updates.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No updates yet.</p>
      ) : (
        <div className="space-y-0">
          {updates.map((u, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${u.color}`} />
                {i < updates.length - 1 && <div className="w-0.5 h-8 bg-slate-200 mt-1" />}
              </div>
              <div className="pb-4">
                <p className="text-xs font-semibold text-slate-800">{u.text}</p>
                <p className="text-[10px] text-slate-500">{u.sub}</p>
                <p className="text-[9px] text-slate-400 mt-0.5">{fmtDate(u.time)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Right sidebar ──────────────────────────────────────────────────────────
function RightSidebar({ order, token, onOrderUpdated }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [updating, setUpdating] = useState(null);

  const OVERALL_TRANSITIONS = {
    placed:            [{ key: "accepted", label: "Accept", color: "#16a34a" }, { key: "rejected", label: "Reject", color: "#dc2626" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
    accepted:          [{ key: "packed", label: "Mark Packed", color: "#0891b2" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
    packed:            [{ key: "shipped", label: "Mark Shipped", color: "#1d4ed8" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
    partially_shipped: [{ key: "shipped", label: "Mark Shipped", color: "#1d4ed8" }, { key: "delivered", label: "Mark Delivered", color: "#16a34a" }],
    shipped:           [{ key: "out_for_delivery", label: "Out for Delivery", color: "#f97316" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
    out_for_delivery:  [{ key: "delivered", label: "Mark Delivered", color: "#16a34a" }],
  };
  const statusActions = OVERALL_TRANSITIONS[order.order_status] || [];

  const handleStatus = async (newStatus) => {
    setUpdating(newStatus); setShowStatusMenu(false);
    try {
      const res = await axios.put(`${API}/admin/orders/${order.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order ${res.data.order_status}`);
      onOrderUpdated({ ...order, order_status: res.data.order_status });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUpdating(null); }
  };

  const total = order.total || order.subtotal || 0;
  const subtotal = order.subtotal || total;
  const shippedCount = (order.items || []).filter(it => it.item_status === "shipped").length;
  const totalItems = (order.items || []).length;

  return (
    <div className="space-y-3 w-72 flex-shrink-0">

      {/* Overall Status Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Order Status</p>
          {statusActions.length > 0 && (
            <div className="relative">
              <button onClick={() => setShowStatusMenu(v => !v)}
                disabled={!!updating}
                className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-indigo-700 border border-slate-200 rounded-lg px-2 py-1 hover:border-indigo-300 transition-colors disabled:opacity-50">
                <Edit2 className="w-2.5 h-2.5" /> Change <ChevronDown className="w-2.5 h-2.5" />
              </button>
              {showStatusMenu && (
                <div className="absolute right-0 top-7 bg-white border border-slate-200 rounded-xl shadow-lg z-20 min-w-[160px] py-1 overflow-hidden">
                  {statusActions.map(a => (
                    <button key={a.key} onClick={() => handleStatus(a.key)}
                      className="w-full text-left text-xs font-semibold px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                      style={{ color: a.color }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.color }} />
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <StatusPill status={order.order_status} size="md" />
        {totalItems > 0 && (
          <p className="text-[10px] text-slate-400 mt-2">{shippedCount} of {totalItems} items shipped</p>
        )}
      </div>

      {/* Shipping Summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Shipping Summary</p>
        <div className="space-y-2.5">
          {[
            { icon: User,   label: "Customer Name", value: order.customer_name },
            { icon: Phone,  label: "Phone",         value: order.customer_phone || "—" },
            { icon: Mail,   label: "Email",         value: order.customer_email?.includes(".local") ? "—" : order.customer_email },
            { icon: MapPin, label: "Address",       value: order.delivery_address },
          ].map(r => (
            <div key={r.label} className="flex items-start gap-2">
              <r.icon className="w-3 h-3 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[9px] text-slate-400">{r.label}</p>
                <p className="text-[11px] font-semibold text-slate-700 break-words">{r.value || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Order Summary</p>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500">Items Total ({totalItems})</span>
            <span className="font-semibold text-slate-700">₹{fmt(subtotal)}</span>
          </div>
          {order.delivery_charge > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-500">Shipping Charge</span>
              <span className="font-semibold text-slate-700">₹{fmt(order.delivery_charge)}</span>
            </div>
          )}
          {order.coupon_discount > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-500">Discount</span>
              <span className="font-semibold text-green-600">-₹{fmt(order.coupon_discount)}</span>
            </div>
          )}
          {order.payment_surcharge > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-500">Surcharge</span>
              <span className="font-semibold text-slate-700">₹{fmt(order.payment_surcharge)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-slate-100 mt-1">
            <span className="font-bold text-slate-900">Total Amount</span>
            <span className="font-black text-indigo-700 text-sm">₹{fmt(total)}</span>
          </div>
        </div>
      </div>

      {/* GST if present */}
      {order.gst_details && (order.gst_details.gstin || order.gst_details.business_name) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2">GST Details</p>
          {order.gst_details.business_name && <p className="text-xs font-semibold text-amber-900">{order.gst_details.business_name}</p>}
          {order.gst_details.gstin && <p className="text-xs font-mono text-amber-800 mt-0.5">{order.gst_details.gstin}</p>}
        </div>
      )}
    </div>
  );
}

// ── Main Order Detail View ─────────────────────────────────────────────────
const TABS = [
  { key: "items",    label: "Items" },
  { key: "customer", label: "Customer & Shipping" },
  { key: "payment",  label: "Payment Details" },
  { key: "notes",    label: "Order Notes" },
  { key: "tracking", label: "Tracking & Updates" },
  { key: "documents",label: "Documents" },
];

export default function OrderDetailView({ order: initialOrder, token, onBack, onRefresh }) {
  const [order, setOrder] = useState(initialOrder);
  const [activeTab, setActiveTab] = useState("items");
  const [showActions, setShowActions] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [updatingOverall, setUpdatingOverall] = useState(null);

  useEffect(() => { setOrder(initialOrder); }, [initialOrder]);

  const handleOrderUpdated = (data) => {
    if (data.items) {
      setOrder(prev => ({ ...prev, ...data, items: data.items }));
    } else {
      setOrder(prev => ({ ...prev, ...data }));
    }
    onRefresh?.();
  };

  const handleOverallStatus = async (newStatus, reason = "") => {
    setUpdatingOverall(newStatus); setShowActions(false);
    try {
      const res = await axios.put(`${API}/admin/orders/${order.id}/status`, { status: newStatus, reason },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order ${ORDER_STATUS_MAP[res.data.order_status]?.label || res.data.order_status}`);
      handleOrderUpdated({ order_status: res.data.order_status });
      setRejectMode(false); setRejectReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUpdatingOverall(null); }
  };

  const sCfg = ORDER_STATUS_MAP[order.order_status] || ORDER_STATUS_MAP.placed;
  const pCfg = PAYMENT_MAP[order.payment_status] || PAYMENT_MAP.pending;
  const shippedCount = (order.items || []).filter(it => it.item_status === "shipped").length;
  const totalItems = (order.items || []).length;
  const total = order.total || order.subtotal || 0;

  return (
    <div className="min-h-full bg-[#f5f7fa]" onClick={() => { setShowActions(false); }}>

      {/* ── Page Header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between gap-4 max-w-[1400px] mx-auto">
          <div>
            <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-1">
              <span>Dashboard</span><span>›</span>
              <button onClick={onBack} className="hover:text-indigo-600 transition-colors">Orders</button>
              <span>›</span>
              <span className="text-slate-600 font-semibold">{order.order_number}</span>
            </div>
            <h1 className="text-xl font-black text-slate-900">Order Details</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onBack}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors"
              data-testid="back-to-orders-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Orders
            </button>
            <button onClick={() => window.open(`/admin/invoice/${order.id}`, "_blank")}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={() => window.open(`/admin/shipping-label/${order.id}`, "_blank")}
              data-testid="order-shipping-label-btn"
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Label
            </button>
            <div className="relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowActions(v => !v)}
                className="flex items-center gap-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3.5 py-2 font-bold transition-colors shadow-sm"
                data-testid="order-actions-btn">
                Actions <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showActions && (
                <div className="absolute right-0 top-9 bg-white border border-slate-200 rounded-xl shadow-xl z-30 min-w-[180px] py-1.5 overflow-hidden">
                  {[
                    { key: "accepted", label: "Accept Order",      color: "#16a34a", from: ["placed"] },
                    { key: "packed",   label: "Mark Packed",       color: "#0891b2", from: ["accepted"] },
                    { key: "shipped",  label: "Mark Shipped",      color: "#1d4ed8", from: ["packed", "partially_shipped"] },
                    { key: "delivered",label: "Mark Delivered",    color: "#16a34a", from: ["shipped", "out_for_delivery", "partially_shipped"] },
                    { key: "cancelled",label: "Cancel Order",      color: "#dc2626", from: ["placed","accepted","packed","shipped","partially_shipped"] },
                    { key: "rejected", label: "Reject Order",      color: "#dc2626", from: ["placed"] },
                  ].filter(a => a.from.includes(order.order_status))
                    .map(a => (
                    <button key={a.key} onClick={() => {
                      if (a.key === "rejected") { setRejectMode(true); setShowActions(false); }
                      else handleOverallStatus(a.key);
                    }}
                      disabled={!!updatingOverall}
                      className="w-full text-left text-xs font-semibold px-4 py-2.5 hover:bg-slate-50 flex items-center gap-2.5 disabled:opacity-50"
                      style={{ color: a.color }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.color }} />
                      {updatingOverall === a.key ? "Updating…" : a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 max-w-[1400px] mx-auto">

        {/* Reject reason form */}
        {rejectMode && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-bold text-red-700 mb-2">Reject Order — provide reason</p>
              <div className="flex gap-2">
                <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection…"
                  className="flex-1 text-xs border border-red-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-red-500 bg-white" />
                <button onClick={() => handleOverallStatus("rejected", rejectReason)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg">Confirm Reject</button>
                <button onClick={() => setRejectMode(false)} className="text-xs text-slate-500 hover:text-slate-700 px-2">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Info Cards Strip ── */}
        <div className="flex gap-3 mb-4 overflow-x-auto pb-1">
          {/* Order ID */}
          <InfoCard label="Order ID" className="min-w-[160px]">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-black text-slate-900">{order.order_number}</span>
              <button onClick={() => { navigator.clipboard.writeText(order.order_number); toast.success("Copied!"); }}
                className="text-slate-400 hover:text-slate-600 transition-colors">
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> {fmtDate(order.created_at)}
            </p>
          </InfoCard>

          {/* Customer */}
          <InfoCard label="Customer" className="min-w-[180px]">
            <p className="text-sm font-bold text-slate-900">{order.customer_name}</p>
            {order.customer_email && !order.customer_email.includes(".local") && (
              <p className="text-[10px] text-slate-400 mt-0.5">{order.customer_email}</p>
            )}
            {order.customer_phone && (
              <p className="text-[10px] text-slate-400">{order.customer_phone}</p>
            )}
          </InfoCard>

          {/* Order Source */}
          <InfoCard label="Order Source" className="min-w-[120px]">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-bold text-slate-800">Website</span>
            </div>
          </InfoCard>

          {/* Payment Status */}
          <InfoCard label="Payment Status" className="min-w-[150px]">
            <StatusPill status={order.payment_status} map={PAYMENT_MAP} size="md" />
            <p className="text-[11px] font-bold text-slate-700 mt-1.5">₹{fmt(total)}</p>
            <p className="text-[10px] text-slate-400">via {order.payment_method || "COD"}</p>
          </InfoCard>

          {/* Order Status */}
          <InfoCard label="Order Status" className="min-w-[170px]">
            <StatusPill status={order.order_status} size="md" />
            <p className="text-[10px] text-slate-400 mt-1.5">{shippedCount} of {totalItems} items shipped</p>
          </InfoCard>

          {/* Total */}
          <InfoCard label="Total Amount" className="min-w-[140px]">
            <p className="text-xl font-black text-indigo-700">₹{fmt(total)}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{totalItems} items</p>
          </InfoCard>
        </div>

        {/* ── Status Stepper ── */}
        <StatusStepper order={order} />

        {/* ── Main: Tabs + Sidebar ── */}
        <div className="flex gap-4 items-start">

          {/* Tabs Column */}
          <div className="flex-1 min-w-0">
            {/* Tab bar */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
              <div className="flex overflow-x-auto">
                {TABS.map((tab, i) => (
                  <button key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`tab-${tab.key}`}
                    className={`flex-shrink-0 px-4 py-3 text-xs font-semibold transition-colors border-b-2 ${
                      activeTab === tab.key
                        ? "border-indigo-600 text-indigo-700 bg-indigo-50/60"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}>
                    {tab.label}
                    {tab.key === "items" && totalItems > 0 && (
                      <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5 font-bold">{totalItems}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            {activeTab === "items" && (
              <ItemsTab order={order} token={token} onOrderUpdated={handleOrderUpdated} />
            )}
            {activeTab === "customer" && <CustomerTab order={order} />}
            {activeTab === "payment" && (
              <PaymentTab order={order} token={token} onOrderUpdated={handleOrderUpdated} />
            )}
            {activeTab === "notes" && (
              <NotesTab order={order} token={token} onOrderUpdated={handleOrderUpdated} />
            )}
            {activeTab === "tracking" && <TrackingTab order={order} />}
            {activeTab === "documents" && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                <Printer className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-semibold text-slate-500 mb-3">Invoice &amp; Documents</p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button onClick={() => window.open(`/admin/invoice/${order.id}`, "_blank")}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors">
                    <Printer className="w-3.5 h-3.5" /> Print Invoice
                  </button>
                  <button onClick={() => window.open(`/admin/shipping-label/${order.id}`, "_blank")}
                    data-testid="documents-shipping-label-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg transition-colors">
                    <Printer className="w-3.5 h-3.5" /> Print Shipping Label
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <RightSidebar order={order} token={token} onOrderUpdated={handleOrderUpdated} />
        </div>
      </div>
    </div>
  );
}
